import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorReference, GraphQlRequestErrorCode } from '../../../../api/types';

interface ErrorTechnicalDetailsProps {
  errorCode: GraphQlRequestErrorCode | null;
  errorReference: ErrorReference | null;
}

const technicalDetailsSx = {
  fontWeight: 'bold',
  color: '#444444',
  fontFamily: 'monospace',
};

/**
 * The support-facing details of an error: a line asking the user to pass them
 * on when reporting the issue, followed by the error code and the error
 * reference. Renders nothing when neither is available, so it is safe to drop
 * into error screens that are also rendered without an underlying error.
 * (though that should hopefully never happen)
 */
export const ErrorTechnicalDetails = (props: ErrorTechnicalDetailsProps) => {
  const { errorCode, errorReference } = props;
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (errorCode === null && errorReference === null) {
    return null;
  }

  const copyErrorReference = () => {
    if (errorReference === null) {
      return;
    }
    const type = 'text/plain';
    const blob = new Blob([errorReference], { type });
    setCopied(true);
    void navigator.clipboard.write([new ClipboardItem({ [type]: blob })]);
  };

  return (
    <>
      <Typography variant="body1" sx={{ mt: 2, maxWidth: '600px' }}>
        {t('pages:errorBoundary.reportGuidance')}
      </Typography>
      {errorCode !== null && (
        <Typography variant="body2" sx={{ ...technicalDetailsSx, mt: 2 }}>
          {t('pages:errorBoundary.errorCodeLabel', { code: errorCode })}
        </Typography>
      )}
      {errorReference !== null && (
        <Typography
          variant="body2"
          sx={{
            ...technicalDetailsSx,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {t('pages:errorBoundary.errorReferenceLabel', {
            ref: errorReference,
          })}
          {copied ? (
            <CheckIcon fontSize="small" />
          ) : (
            <ContentCopyIcon
              fontSize="small"
              color="primary"
              onClick={copyErrorReference}
              style={{ cursor: 'pointer' }}
            />
          )}
        </Typography>
      )}
    </>
  );
};

export default ErrorTechnicalDetails;
